import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";

const LINKS = [
  { to: "/", label: "Treasury" },
  { to: "/lab", label: "Hussar Lab" },
  { to: "/intelligence", label: "Weekly Intelligence" },
  { to: "/governance", label: "Governance" },
];

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <header
      data-testid="main-nav"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? "glass-panel border-b border-hussar-gold/10" : "bg-transparent"
      }`}
    >
      <div className="px-6 sm:px-10 lg:px-16 max-w-[1600px] mx-auto flex items-center justify-between h-20">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-3 group">
          <div className="w-9 h-9 border border-hussar-gold/50 flex items-center justify-center transition-colors duration-300 group-hover:border-hussar-gold">
            <span className="font-serif text-hussar-gold text-lg leading-none">H</span>
          </div>
          <div className="leading-tight">
            <div className="font-serif text-hussar-ink text-sm tracking-[0.18em]">HUSSAR</div>
            <div className="font-mono text-[9px] uppercase tracking-[0.35em] text-hussar-muted">Treasury</div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-10">
          {LINKS.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={`font-mono text-[11px] uppercase tracking-[0.22em] transition-colors duration-300 ${
                location.pathname === l.to ? "text-hussar-gold" : "text-hussar-slate hover:text-hussar-ink"
              }`}
            >
              {l.label}
            </Link>
          ))}
          <Link to="/request-access" data-testid="nav-request-access-btn" className="btn-gold !px-6 !py-3">
            Request Early Access
          </Link>
        </nav>

        <button
          data-testid="nav-mobile-toggle"
          className="lg:hidden text-hussar-ink p-2"
          onClick={() => setOpen(!open)}
          aria-label="Toggle menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden glass-panel border-b border-hussar-gold/10 overflow-hidden"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {LINKS.map((l) => (
                <Link
                  key={l.to}
                  to={l.to}
                  data-testid={`nav-mobile-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className="font-mono text-xs uppercase tracking-[0.22em] text-hussar-slate hover:text-hussar-gold"
                >
                  {l.label}
                </Link>
              ))}
              <Link to="/request-access" data-testid="nav-mobile-request-access-btn" className="btn-gold w-fit">
                Request Early Access
              </Link>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
